import { OnboardingShell } from '@/components/layout/OnboardingShell';
import { StepAdmin } from '@/components/onboarding/StepAdmin';
import { StepDb } from '@/components/onboarding/StepDb';
import { StepFinish } from '@/components/onboarding/StepFinish';
import { StepLlm } from '@/components/onboarding/StepLlm';
import { StepStorage } from '@/components/onboarding/StepStorage';
import { api } from '@/lib/api';
import type { OnboardingStep } from '@innermate/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

const STEP_META: Record<
  Exclude<OnboardingStep, 'done'> | 'finish',
  { title: string; description: string }
> = {
  db: {
    title: '시스템 데이터베이스 연결',
    description:
      'innermate 가 자체 메타데이터를 저장할 PostgreSQL 정보를 입력해 주세요. docker-compose 의 기본값이 미리 채워져 있어요.',
  },
  admin: {
    title: '관리자 계정 만들기',
    description: '첫 관리자입니다. 이후 관리 콘솔에서 사용자/엔드포인트를 관리할 수 있어요.',
  },
  llm: {
    title: 'LLM 엔드포인트 등록',
    description: 'OpenAI 호환 API 라면 어디든 OK — mlx, vLLM, Ollama, OpenAI 모두 지원합니다.',
  },
  storage: {
    title: '오브젝트 스토리지 (선택)',
    description:
      '엑셀 등 사용자 업로드 파일을 저장합니다. 나중에 관리자 대시보드에서 추가/수정할 수 있어요.',
  },
  finish: {
    title: '거의 다 됐어요',
    description: '마지막으로 부트스트랩을 잠그고 정상 모드로 전환합니다.',
  },
};

interface Props {
  step: 'db' | 'admin' | 'llm' | 'storage' | 'finish';
}

export function Onboarding({ step }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: api.onboardingStatus,
    refetchOnWindowFocus: false,
  });

  // 서버가 알려주는 nextStep 과 URL 의 step 이 다르면 보정
  useEffect(() => {
    const data = status.data;
    if (!data) return;
    if (data.mode === 'normal') {
      navigate({ to: '/login' });
      return;
    }
    if (data.mode === 'licensing') {
      navigate({ to: '/licensing' });
      return;
    }
    const nextUrlStep: Props['step'] = data.step === 'done' ? 'finish' : data.step;
    if (nextUrlStep !== step) {
      navigate({ to: '/onboarding/$step', params: { step: nextUrlStep } });
    }
  }, [status.data, step, navigate]);

  const meta = STEP_META[step];

  const onAdvance = async () => {
    await qc.invalidateQueries({ queryKey: ['onboarding-status'] });
  };

  return (
    <OnboardingShell current={step} title={meta.title} description={meta.description}>
      {step === 'db' && <StepDb onAdvance={onAdvance} />}
      {step === 'admin' && <StepAdmin onAdvance={onAdvance} />}
      {step === 'llm' && <StepLlm onAdvance={onAdvance} />}
      {step === 'storage' && <StepStorage onAdvance={onAdvance} />}
      {step === 'finish' && (
        <StepFinish
          onAdvance={async () => {
            await qc.invalidateQueries({ queryKey: ['onboarding-status'] });
            navigate({ to: '/login' });
          }}
        />
      )}
    </OnboardingShell>
  );
}
